import { useState, useEffect, useMemo } from 'react';
import { Input } from '@/components/base/input/input';
import { TextArea } from '@/components/base/textarea/textarea';
import { Button } from '@/components/base/buttons/button';
import { MultiSelectBase } from '@/components/base/select/multi-select';
import { SelectItem } from '@/components/base/select/select-item';
import { useListData } from 'react-stately';
import type { SelectItemType } from '@/components/base/select/select';
import { addressBookService } from '@/data/mock-address-book';
import type { AddressBookEntry } from '@/types/address-book';
import type { ExtractedData, ShipperQuoteRequest, CreateShipperQuoteRequestData, UpdateShipperQuoteRequestData } from '@/types/quote';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@/components/application/date-picker/date-picker';
import { getLocalTimeZone, CalendarDate } from '@internationalized/date';
import type { DateValue } from 'react-aria-components';
import { Label } from '@/components/base/input/label';

interface ShipperQuoteFormProps {
    quoteRequest?: ShipperQuoteRequest;
    onSubmit: (data: CreateShipperQuoteRequestData | UpdateShipperQuoteRequestData) => void;
    onCancel: () => void;
    shipperId: string;
}

export const ShipperQuoteForm = ({ quoteRequest, onSubmit, onCancel, shipperId }: ShipperQuoteFormProps) => {
    const { t } = useTranslation(['pages:quotes', 'common']);
    const [subject, setSubject] = useState(quoteRequest?.subject || '');
    const [body, setBody] = useState(quoteRequest?.body || '');
    const [product, setProduct] = useState(quoteRequest?.extractedData?.product || '');
    const [origin, setOrigin] = useState(quoteRequest?.extractedData?.origin || '');
    const [destination, setDestination] = useState(quoteRequest?.extractedData?.destination || '');
    const [quantity, setQuantity] = useState(quoteRequest?.extractedData?.quantity?.toString() || '');
    const [unit, setUnit] = useState(quoteRequest?.extractedData?.unit || '');
    const toCalendarDate = (date?: Date | null): CalendarDate | null => {
        if (!date) return null;
        // Convert to ISO date string (YYYY-MM-DD) in local time
        const year = date.getFullYear();
        const month = date.getMonth() + 1; // JS months are 0-based
        const day = date.getDate();
        return new CalendarDate(year, month, day);
    };

    const [pickupDate, setPickupDate] = useState<CalendarDate | null>(
        toCalendarDate(quoteRequest?.extractedData?.pickupDate)
    );
    const [deliveryDate, setDeliveryDate] = useState<CalendarDate | null>(
        toCalendarDate(quoteRequest?.extractedData?.deliveryDate)
    );

    // Helper per convertire DateValue in CalendarDate
    const toCalendarDateFromValue = (value: DateValue | null): CalendarDate | null => {
        if (!value) return null;
        // Se è già un CalendarDate, ritorna direttamente
        if (value instanceof CalendarDate) {
            return value;
        }
        // Altrimenti estrai year, month, day e crea un nuovo CalendarDate
        return new CalendarDate(value.year, value.month, value.day);
    };
    const [temperatureRequirements, setTemperatureRequirements] = useState(quoteRequest?.extractedData?.temperatureRequirements || '');
    const [specialRequirements, setSpecialRequirements] = useState(quoteRequest?.extractedData?.specialRequirements?.join(', ') || '');

    // Carica freight forwarder dall'address book
    const [freightForwarders, setFreightForwarders] = useState<AddressBookEntry[]>([]);
    const selectedRecipients = useListData<SelectItemType>({
        initialItems: [],
    });

    useEffect(() => {
        const loadFreightForwarders = () => {
            const entries = addressBookService.search({
                accountId: shipperId,
                role: 'Freight Forwarder',
            });
            setFreightForwarders(entries);

            // Se è una modifica, carica i recipient selezionati
            if (quoteRequest) {
                const selected = entries
                    .filter((entry) => quoteRequest.recipients.includes(entry.id))
                    .map((entry) => ({
                        id: entry.id,
                        label: `${entry.firstName} ${entry.lastName}`,
                        supportingText: entry.email,
                    }));
                // Rimuovi tutti gli item esistenti e aggiungi quelli selezionati
                // Clear all existing items and add the selected ones
                while (selectedRecipients.items.length > 0) {
                    selectedRecipients.remove(selectedRecipients.items[0].id);
                }
                selected.forEach(item => selectedRecipients.append(item));
            }
        };
        loadFreightForwarders();
    }, [shipperId, quoteRequest]);

    const freightForwarderOptions: SelectItemType[] = useMemo(() => {
        return freightForwarders.map((entry) => ({
            id: entry.id,
            label: `${entry.firstName} ${entry.lastName}`,
            supportingText: entry.email,
        }));
    }, [freightForwarders]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!subject.trim() || !body.trim() || selectedRecipients.items.length === 0) {
            return;
        }

        const extractedData: ExtractedData = {
            product: product || undefined,
            origin: origin || undefined,
            destination: destination || undefined,
            quantity: quantity ? parseFloat(quantity) : undefined,
            unit: unit || undefined,
            pickupDate: pickupDate ? pickupDate.toDate(getLocalTimeZone()) : undefined,
            deliveryDate: deliveryDate ? deliveryDate.toDate(getLocalTimeZone()) : undefined,
            temperatureRequirements: temperatureRequirements || undefined,
            specialRequirements: specialRequirements ? specialRequirements.split(',').map((s) => s.trim()).filter(Boolean) : undefined,
        };

        if (quoteRequest) {
            // Update
            onSubmit({
                subject,
                body,
                recipients: selectedRecipients.items.map((item) => item.id),
                extractedData,
            });
        } else {
            // Create
            onSubmit({
                subject,
                body,
                recipients: selectedRecipients.items.map((item) => item.id),
                extractedData,
                shipperId,
            });
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Subject */}
            <div>
                <Input
                    label={t('shipper.form.subject') || 'Oggetto'}
                    placeholder={t('shipper.form.subjectPlaceholder') || 'Es. Richiesta preventivo trasporto...'}
                    value={subject}
                    onChange={setSubject}
                    isRequired
                    size="md"
                />
            </div>

            {/* Body */}
            <div>
                <label className="text-sm font-medium text-primary mb-2 block">
                    {t('shipper.form.body') || 'Corpo Email'} <span className="text-error-600">*</span>
                </label>
                <TextArea
                    placeholder={t('shipper.form.bodyPlaceholder') || 'Inserisci il testo della richiesta di preventivo...'}
                    value={body}
                    onChange={setBody}
                    rows={8}
                />
            </div>

            {/* Recipients - Multi-select */}
            <div>
                <MultiSelectBase
                    label={t('shipper.form.recipients') || 'Destinatari (Freight Forwarder)'}
                    placeholder={t('shipper.form.recipientsPlaceholder') || 'Seleziona freight forwarder...'}
                    items={freightForwarderOptions}
                    selectedItems={selectedRecipients}
                    size="md"
                    isRequired
                >
                    {(item) => (
                        <SelectItem key={item.id} id={item.id}>
                            {item.label}
                            {item.supportingText && <span className="text-tertiary"> - {item.supportingText}</span>}
                        </SelectItem>
                    )}
                </MultiSelectBase>
            </div>

            {/* Extracted Data Section */}
            <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary">
                <h3 className="text-lg font-semibold text-primary mb-6">
                    {t('shipper.form.extractedDataTitle') || 'Dati della Richiesta'}
                </h3>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Input
                        label={t('extracted.product') || 'Prodotto'}
                        placeholder={t('extracted.product') || 'Prodotto'}
                        value={product}
                        onChange={setProduct}
                        size="md"
                    />
                    <Input
                        label={t('extracted.origin') || 'Origine'}
                        placeholder={t('extracted.origin') || 'Origine'}
                        value={origin}
                        onChange={setOrigin}
                        size="md"
                    />
                    <Input
                        label={t('extracted.destination') || 'Destinazione'}
                        placeholder={t('extracted.destination') || 'Destinazione'}
                        value={destination}
                        onChange={setDestination}
                        size="md"
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            label={t('extracted.quantity') || 'Quantità'}
                            placeholder={t('extracted.quantity') || 'Quantità'}
                            value={quantity}
                            onChange={setQuantity}
                            type="number"
                            size="md"
                        />
                        <Input
                            label={t('extracted.unit') || 'Unità'}
                            placeholder={t('extracted.unit') || 'kg, container, ecc.'}
                            value={unit}
                            onChange={setUnit}
                            size="md"
                        />
                    </div>
                    <div>
                        <Label>{t('extracted.pickupDate') || 'Data Ritiro'}</Label>
                        <DatePicker
                            value={pickupDate}
                            onChange={(value) => setPickupDate(toCalendarDateFromValue(value))}
                        />
                    </div>
                    <div>
                        <Label>{t('extracted.deliveryDate') || 'Data Consegna'}</Label>
                        <DatePicker
                            value={deliveryDate}
                            onChange={(value) => setDeliveryDate(toCalendarDateFromValue(value))}
                        />
                    </div>
                    <Input
                        label={t('extracted.temperatureRequirements') || 'Requisiti Temperatura'}
                        placeholder={t('extracted.temperatureRequirements') || 'Es. 4-8°C'}
                        value={temperatureRequirements}
                        onChange={setTemperatureRequirements}
                        size="md"
                    />
                    <Input
                        label={t('extracted.specialRequirements') || 'Requisiti Speciali'}
                        placeholder={t('extracted.specialRequirements') || 'Separati da virgola'}
                        value={specialRequirements}
                        onChange={setSpecialRequirements}
                        size="md"
                    />
                </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
                <Button
                    type="button"
                    onClick={onCancel}
                    color="secondary"
                    size="md"
                >
                    {t('common:cancel') || 'Annulla'}
                </Button>
                <Button
                    type="submit"
                    color="primary"
                    size="md"
                    isDisabled={!subject.trim() || !body.trim() || selectedRecipients.items.length === 0}
                >
                    {quoteRequest ? (t('shipper.form.update') || 'Aggiorna') : (t('shipper.form.send') || 'Invia Richiesta')}
                </Button>
            </div>
        </form>
    );
};

